import { type FC, useEffect, useMemo, useState } from "react";

import { CourseSelectionList } from "../content/courses/CourseSelectionList";

import { serverManageCourseAdmin } from "api/api-server-actions";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { ORDER_BY_CODE } from "mds/hooks/use-ordered";
import { slices, storeApi, useAppDispatch, useAppSelector } from "store/index";
import { courseUserSelectors } from "store/selectors";
import { selectCoursesWithGrouping } from "store/selectors/section";
import { toastLocalizedOperationCatcher, toastSuccessMessage } from "utils/alerts";

type ManageCourseAdminModalProps = {
  userId: string;
  userName: string;
  onClose: () => void;
};

export const ManageCourseAdminModal: FC<ManageCourseAdminModalProps> = ({
  userId,
  userName,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const unsortedCourses = useAppSelector(selectCoursesWithGrouping);
  const courses = useMemo(() => unsortedCourses.toSorted(ORDER_BY_CODE), [unsortedCourses]);
  const existingCourseUsers = useAppSelector((state) =>
    courseUserSelectors.filterByUserId(state, userId),
  );

  const existingCourseIds = useMemo(
    () => existingCourseUsers.map((cu) => cu.course_id),
    [existingCourseUsers],
  );

  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>(existingCourseIds);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The user is "currently a course admin" iff they have any CourseUser rows.
  const wasCourseAdmin = existingCourseIds.length > 0;
  const isRemovingCourseAdminAccess = wasCourseAdmin && selectedCourseIds.length === 0;

  useEffect(() => {
    storeApi.courses.list();
  }, [userId]);

  useEffect(() => {
    setSelectedCourseIds(existingCourseIds);
  }, [existingCourseIds]);

  const toggleCourseChecked = (courseId: string) => {
    setSelectedCourseIds((prev) =>
      prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [...prev, courseId],
    );
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      const updatedCourseUsers = await serverManageCourseAdmin(userId, selectedCourseIds);

      const removeCourseUsers = existingCourseUsers.filter(
        (cu) => !updatedCourseUsers.some((newCu) => newCu.id === cu.id),
      );

      // TODO: consider adding a method to removeMany op.
      removeCourseUsers.forEach((cu) =>
        dispatch(slices.course_users.actions.removeOne({ id: cu.id })),
      );
      dispatch(slices.course_users.actions.updateMany(updatedCourseUsers));
      toastSuccessMessage(t("user_options_menu.manage_course_admin.success"));
      onClose();
    } catch (error) {
      toastLocalizedOperationCatcher("manage_course_admin")(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="-mt-2 flex flex-col gap-4">
      <div className="text-sm font-medium text-black-tint-55">{userName}</div>

      <div className="text-sm font-normal">
        {t("user_options_menu.manage_course_admin.description")}
      </div>

      <div className="flex flex-col gap-2">
        <div className="font-medium">
          {t("user_options_menu.manage_course_admin.select_courses")}
        </div>

        {courses.length > 0 ? (
          <CourseSelectionList
            courses={courses}
            selectedCourseIds={selectedCourseIds}
            onCourseToggle={toggleCourseChecked}
          />
        ) : (
          <div className="text-black-tint-30">
            {t("user_options_menu.manage_course_admin.no_courses")}
          </div>
        )}

        {isRemovingCourseAdminAccess ? (
          <div className="text-xs text-black-tint-55 text-red-shade-20">
            {t("user_options_menu.manage_course_admin.no_courses_warning")}
          </div>
        ) : (
          <div className="text-xs text-black-tint-55">
            {t("user_options_menu.manage_course_admin.course_admin_of_count", {
              count: selectedCourseIds.length,
            })}
          </div>
        )}
      </div>

      <div className="flex w-full justify-end gap-2">
        <Button kind="white" size="s" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </Button>

        <Button
          disabled={
            (selectedCourseIds.length === 0 && !isRemovingCourseAdminAccess) || isSubmitting
          }
          kind="blue"
          size="s"
          title={t("user_options_menu.manage_course_admin.submit")}
          onClick={handleSubmit}
        >
          {t("user_options_menu.manage_course_admin.submit")}
        </Button>
      </div>
    </div>
  );
};
